import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Camera, CameraOff, CheckCircle2, ClipboardList, Loader2, Plus, ScanLine, Search, Trash2, XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { fdatetime, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/crm/LeadForm";
import { cn } from "@/lib/utils";

const TONE = { "Pending Approval": "warning", Approved: "success", Rejected: "danger" };

const Variance = ({ v }) => (
  <span className={cn("num text-sm font-bold", v === 0 ? "text-muted-foreground" : v > 0 ? "text-success" : "text-destructive")}>
    {v > 0 ? "+" : ""}{v}
  </span>
);

export const StockAuditPanel = ({ stock, audits, onDone }) => {
  const { isAdmin, can } = useAuth();
  const [countOpen, setCountOpen] = useState(false);
  const [q, setQ] = useState("");
  const [counts, setCounts] = useState({});
  const [lineRemarks, setLineRemarks] = useState({});
  const [unmatched, setUnmatched] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [code, setCode] = useState("");
  const [camOn, setCamOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [decisionRemarks, setDecisionRemarks] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const lastRef = useRef({ code: "", at: 0 });

  const byId = useMemo(() => Object.fromEntries(stock.map((r) => [r.product_id, r])), [stock]);
  const entered = Object.entries(counts).filter(([, v]) => v !== "" && v !== undefined && v !== null);

  const stopCam = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((t) => t.stop());
    streamRef.current = null;
    setCamOn(false);
  }, []);

  useEffect(() => () => stopCam(), [stopCam]);
  useEffect(() => { if (!countOpen) stopCam(); }, [countOpen, stopCam]);

  const addScan = useCallback((raw) => {
    const value = String(raw || "").trim();
    if (!value) return;
    const hit = stock.find((r) => String(r.barcode) === value
      || String(r.sku).toLowerCase() === value.toLowerCase());
    if (!hit) {
      setUnmatched((u) => (u.includes(value) ? u : [...u, value]));
      toast.error(`No product matches ${value}. Add it manually below.`);
      return;
    }
    setCounts((c) => ({ ...c, [hit.product_id]: Number(c[hit.product_id] || 0) + 1 }));
    toast.success(`${hit.product_name} +1`);
  }, [stock]);

  const startCam = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      return toast.error("This device or browser does not allow camera access.");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: { ideal: "environment" } } });
      streamRef.current = stream;
      setCamOn(true);
      setTimeout(() => { if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); } }, 60);
      if (!("BarcodeDetector" in window)) {
        return toast.info("Live detection is not supported here. Use a scanner or type the code.");
      }
      const det = new window.BarcodeDetector({
        formats: ["code_128", "ean_13", "ean_8", "code_39", "upc_a", "qr_code"],
      });
      const tick = async () => {
        if (!streamRef.current || !videoRef.current) return;
        try {
          const found = await det.detect(videoRef.current);
          if (found?.length) {
            const v = found[0].rawValue;
            const now = Date.now();
            if (!(lastRef.current.code === v && now - lastRef.current.at < 1500)) {
              lastRef.current = { code: v, at: now };
              addScan(v);
            }
          }
        } catch { /* keep trying */ }
        setTimeout(tick, 300);
      };
      tick();
    } catch {
      toast.error("Camera permission was refused.");
    }
  };

  const manualMatches = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return stock.filter((r) => `${r.product_name} ${r.sku} ${r.category}`.toLowerCase().includes(t)).slice(0, 8);
  }, [stock, q]);

  const reset = () => {
    setCounts({}); setLineRemarks({}); setRemarks(""); setUnmatched([]); setQ(""); setCode("");
  };

  const submitCount = async () => {
    if (!entered.length) return toast.error("Scan or enter the counted quantity for at least one product.");
    setBusy(true);
    try {
      await api.post("/stock-audits", {
        remarks: [remarks, unmatched.length ? `Codes that did not scan: ${unmatched.join(", ")}` : ""]
          .filter(Boolean).join(" \u00b7 "),
        items: entered.map(([product_id, physical_qty]) => ({
          product_id, physical_qty: Number(physical_qty), remarks: lineRemarks[product_id] || "",
        })),
      });
      toast.success("Stock count submitted for approval");
      setCountOpen(false); reset();
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const decide = async (approve) => {
    if (!approve && !decisionRemarks.trim()) return toast.error("Please add a remark before rejecting.");
    setBusy(true);
    try {
      await api.post(`/stock-audits/${detail.id}/${approve ? "approve" : "reject"}`,
        { remarks: decisionRemarks, adjust_stock: true });
      toast.success(`Audit ${approve ? "approved and stock corrected" : "rejected"}`);
      setDetail(null); setDecisionRemarks("");
      onDone?.();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const columns = [
    { key: "number", label: "Audit", render: (r) => <span className="num text-sm font-semibold">{r.number}</span> },
    { key: "created_by_name", label: "Counted by", render: (r) => <span className="text-sm">{r.created_by_name}</span> },
    { key: "counted_lines", label: "Products", align: "right", render: (r) => <span className="num text-sm">{r.counted_lines}</span> },
    {
      key: "variance_lines", label: "Mismatches", align: "right",
      render: (r) => <span className={cn("num text-sm font-bold", r.variance_lines ? "text-destructive" : "text-muted-foreground")}>{r.variance_lines}</span>,
    },
    { key: "value_impact", label: "Value impact", align: "right", render: (r) => <span className="num text-sm">{money(r.value_impact)}</span>, hideMobile: true },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} tone={TONE[r.status]} /> },
    { key: "created_at", label: "Submitted", align: "right", render: (r) => <span className="text-xs text-muted-foreground">{fdatetime(r.created_at)}</span>, hideMobile: true },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-primary/25 bg-primary-soft/40 px-4 py-3 sm:flex-row sm:items-center">
        <ClipboardList className="h-5 w-5 shrink-0 text-primary" />
        <p className="flex-1 text-sm text-foreground/80">
          Scan every sticker on the shelf, or type the counts by hand. The system compares your batch with
          the book stock, and an administrator approves or rejects it before any stock is corrected.
        </p>
        {can("stock_audit", "add") && (
          <Button onClick={() => setCountOpen(true)} data-testid="start-audit-btn">
            <ScanLine className="h-4 w-4" /> Start a stock count
          </Button>
        )}
      </div>

      <DataTable columns={columns} rows={audits} onRowClick={(r) => { setDetail(r); setDecisionRemarks(""); }}
        empty={<EmptyState icon={ClipboardList} title="No stock audits yet"
          description="Start a physical count and it will appear here for approval." />} />

      {/* Count sheet */}
      <Dialog open={countOpen} onOpenChange={setCountOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Physical stock count</DialogTitle>
            <DialogDescription>
              Scan a barcode once for every piece you find, or add products manually. Everything you count
              is submitted together as one batch and compared with the system stock.
            </DialogDescription>
          </DialogHeader>

          {/* Scanner */}
          <div className="space-y-3 rounded-xl border border-primary/25 bg-primary-soft/40 p-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <Field label="Scan or type a barcode / SKU" hint="Each scan adds one piece to the count.">
                  <form onSubmit={(e) => { e.preventDefault(); addScan(code); setCode(""); }}>
                    <Input autoFocus value={code} onChange={(e) => setCode(e.target.value)}
                      className="num h-11 tracking-widest" placeholder="8901234567890 or RBC-4500"
                      data-testid="audit-scan-input" />
                  </form>
                </Field>
              </div>
              <Button type="button" variant={camOn ? "outline" : "soft"} onClick={() => (camOn ? stopCam() : startCam())}
                data-testid="audit-camera-toggle">
                {camOn ? <><CameraOff className="h-4 w-4" /> Stop camera</> : <><Camera className="h-4 w-4" /> Scan with camera</>}
              </Button>
            </div>

            {camOn && (
              <div className="relative overflow-hidden rounded-xl border border-primary/40 bg-foreground/90">
                <video ref={videoRef} playsInline muted className="h-44 w-full object-cover" />
                <div className="pointer-events-none absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2 bg-primary/80" />
                <p className="absolute bottom-2 left-0 right-0 text-center text-[11px] text-primary-foreground">
                  Keep scanning &mdash; every sticker adds one piece
                </p>
              </div>
            )}

            {unmatched.length > 0 && (
              <div className="flex flex-wrap items-center gap-2" data-testid="audit-unmatched">
                <span className="text-xs font-semibold text-destructive">Did not match any product:</span>
                {unmatched.map((u) => (
                  <span key={u} className="num flex items-center gap-1 rounded-full border border-destructive/40 bg-card px-2 py-0.5 text-xs">
                    {u}
                    <button onClick={() => setUnmatched(unmatched.filter((x) => x !== u))} className="text-muted-foreground hover:text-destructive">
                      <XCircle className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Counted batch */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Counted in this batch {entered.length > 0 && `(${entered.length})`}
            </p>
            {entered.length === 0 ? (
              <p className="rounded-lg border border-dashed border-border px-4 py-6 text-center text-sm text-muted-foreground">
                Nothing counted yet. Scan a sticker or add a product manually below.
              </p>
            ) : entered.map(([pid, v]) => {
              const r = byId[pid] || {};
              const variance = Number(v) - Number(r.qty || 0);
              return (
                <div key={pid} className="grid items-end gap-3 rounded-xl border border-border bg-muted/30 p-3 sm:grid-cols-12">
                  <div className="min-w-0 sm:col-span-4">
                    <p className="truncate text-sm font-semibold">{r.product_name}</p>
                    <p className="num truncate text-xs text-muted-foreground">{r.sku} &middot; {r.category}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">System</p>
                    <p className={cn("num text-sm font-bold", r.qty < 0 && "text-destructive")}>{r.qty}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Counted">
                      <Input type="number" value={v} data-testid={`audit-qty-${r.sku}`}
                        onChange={(e) => setCounts({ ...counts, [pid]: e.target.value })} />
                    </Field>
                  </div>
                  <div className="sm:col-span-2">
                    <Field label="Remarks">
                      <Input value={lineRemarks[pid] || ""}
                        onChange={(e) => setLineRemarks({ ...lineRemarks, [pid]: e.target.value })} />
                    </Field>
                  </div>
                  <div className="flex items-center justify-between gap-2 sm:col-span-2">
                    <Variance v={variance} />
                    <Button type="button" variant="ghost" size="iconSm" className="text-destructive"
                      data-testid={`audit-remove-${r.sku}`}
                      onClick={() => {
                        const c = { ...counts }; delete c[pid]; setCounts(c);
                      }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Manual add */}
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Add a product manually
            </p>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="audit-search"
                placeholder="Search by product name, SKU or category (for stickers that will not scan)" />
            </div>
            {manualMatches.map((r) => (
              <div key={r.product_id} className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold">{r.product_name}</p>
                  <p className="num truncate text-xs text-muted-foreground">{r.sku} &middot; system {r.qty}</p>
                </div>
                <Button type="button" size="sm" variant="soft" data-testid={`audit-add-${r.sku}`}
                  onClick={() => setCounts((c) => ({ ...c, [r.product_id]: Number(c[r.product_id] || 0) + 1 }))}>
                  <Plus className="h-3.5 w-3.5" /> Add
                </Button>
              </div>
            ))}
          </div>

          <Field label="Overall remarks">
            <Textarea rows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)}
              data-testid="audit-remarks" placeholder="Where was the count done, who was present, anything unusual" />
          </Field>

          <DialogFooter>
            <span className="num mr-auto text-xs text-muted-foreground">{entered.length} product(s) counted</span>
            <Button variant="outline" onClick={() => setCountOpen(false)}>Cancel</Button>
            <Button onClick={submitCount} disabled={busy || !entered.length} data-testid="audit-submit">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} Submit for approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Audit detail + decision */}
      <Dialog open={!!detail} onOpenChange={(v) => { if (!v) setDetail(null); }}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{detail.number}</DialogTitle>
                <DialogDescription>
                  Counted by {detail.created_by_name} on {fdatetime(detail.created_at)} &middot;{" "}
                  {detail.variance_lines} mismatch(es) worth {money(detail.value_impact)}
                </DialogDescription>
              </DialogHeader>

              <div className="flex items-center gap-3">
                <StatusBadge status={detail.status} tone={TONE[detail.status]} />
                {detail.stock_adjusted && <StatusBadge status="Stock corrected" tone="success" dot={false} />}
              </div>

              {detail.remarks && <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">{detail.remarks}</p>}

              <div className="space-y-2">
                {detail.items.map((l) => (
                  <div key={l.product_id} className={cn("flex items-center gap-3 rounded-lg border p-3",
                    l.variance ? "border-destructive/30 bg-destructive-soft/40" : "border-border bg-muted/30")}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{l.product_name}</p>
                      <p className="num truncate text-xs text-muted-foreground">{l.sku}{l.remarks ? ` \u00b7 ${l.remarks}` : ""}</p>
                    </div>
                    <span className="num text-xs text-muted-foreground">system {l.system_qty}</span>
                    <span className="num text-xs text-muted-foreground">counted {l.physical_qty}</span>
                    <Variance v={l.variance} />
                  </div>
                ))}
              </div>

              {detail.decision_by_name && (
                <p className="rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm">
                  <b>{detail.decision_by_name}</b> {detail.status.toLowerCase()} this audit on {fdatetime(detail.decided_at)}.
                  {detail.decision_remarks ? ` ${detail.decision_remarks}` : ""}
                </p>
              )}

              {isAdmin && detail.status === "Pending Approval" && (
                <>
                  <Field label="Your remarks" hint="Required when rejecting a count.">
                    <Textarea rows={2} value={decisionRemarks} data-testid="decision-remarks"
                      onChange={(e) => setDecisionRemarks(e.target.value)} />
                  </Field>
                  <DialogFooter>
                    <Button variant="outline" className="text-destructive" disabled={busy}
                      onClick={() => decide(false)} data-testid="audit-reject">
                      <XCircle className="h-4 w-4" /> Reject
                    </Button>
                    <Button variant="success" disabled={busy} onClick={() => decide(true)} data-testid="audit-approve">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                      Approve and correct stock
                    </Button>
                  </DialogFooter>
                </>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
